/**
 * UI-стейт страницы репо: что открыто в drawer'ах, что подсвечено,
 * состояние sidebar'а на mobile.
 *
 * Сохраняется только в памяти (deep-link состояние держится в URL —
 * `use-repo-url-state` + параметр `entity` синхронизируется EntityCard'ом).
 *
 * Зачем Zustand, а не useState/Context: drawer'ы открываются из
 * разных мест дерева (NarrativeBlockView, GlossarySidebar, TopBar).
 * Прокидывать callback через 4-5 уровней — дорого; Context на каждое
 * drawer-состояние — перерендеры. Один store решает оба.
 *
 * Карточка сущности ведёт браузероподобную историю (стек + индекс):
 * клик по связи «проваливается» глубже, назад/вперёд — навигация по
 * стеку без потери пути. closeCard очищает стек.
 */

import { create } from 'zustand';
import type { EntityRef } from '@pflow/shared/v2';

export interface ChatRequest {
    /** Монотонный счётчик — каждый запрос открывает чат заново. */
    seq:     number;
    /** Текст, который подставить в композер (не отправляя). */
    prefill: string;
}

export interface UiState {
    /** Сущность, открытая в EntityCard drawer'е (или null — закрыт). */
    cardEntity: EntityRef | null;
    /** История навигации по карточкам (стек) и позиция в ней. */
    cardStack: EntityRef[];
    cardIndex: number;
    /**
     * Счётчик ВСЕХ вызовов openCard (включая клик по уже открытой сущности,
     * который дедупится и не меняет cardEntity) — мобильный чат закрывается
     * по нему, иначе повторный клик по упоминанию «мёртвый».
     */
    cardOpenSeq: number;
    /** Якорь, который надо подсветить flash-анимацией после scroll. */
    flashAnchor: string | null;
    /** Открыт ли мобильный sidebar-drawer. */
    sidebarOpen: boolean;
    /** Внешний запрос на открытие чата (кнопка «спросить агента», выделение). */
    chatRequest: ChatRequest | null;

    openCard:    (ref: EntityRef) => void;
    backCard:    () => void;
    forwardCard: () => void;
    closeCard:   () => void;
    triggerFlash: (anchor: string) => void;
    clearFlash:  () => void;
    openSidebar: () => void;
    closeSidebar: () => void;
    requestChat: (prefill: string) => void;
}

function sameRef(a: EntityRef | undefined, b: EntityRef): boolean {
    return !!a && a.type === b.type && a.name === b.name && (a.canvas ?? '') === (b.canvas ?? '');
}

export const useUiStore = create<UiState>((set, get) => ({
    cardEntity:  null,
    cardStack:   [],
    cardIndex:   -1,
    cardOpenSeq: 0,
    flashAnchor: null,
    sidebarOpen: false,
    chatRequest: null,

    openCard: ref => {
        const { cardStack, cardIndex, cardOpenSeq } = get();
        if (sameRef(cardStack[cardIndex], ref)) {
            set({ cardOpenSeq: cardOpenSeq + 1 }); // повторный клик по той же
            return;
        }
        const stack = [...cardStack.slice(0, cardIndex + 1), ref];
        set({ cardStack: stack, cardIndex: stack.length - 1, cardEntity: ref, cardOpenSeq: cardOpenSeq + 1 });
    },
    backCard: () => {
        const { cardStack, cardIndex } = get();
        if (cardIndex <= 0) return;
        set({ cardIndex: cardIndex - 1, cardEntity: cardStack[cardIndex - 1] });
    },
    forwardCard: () => {
        const { cardStack, cardIndex } = get();
        if (cardIndex >= cardStack.length - 1) return;
        set({ cardIndex: cardIndex + 1, cardEntity: cardStack[cardIndex + 1] });
    },
    closeCard: () => set({ cardEntity: null, cardStack: [], cardIndex: -1 }),

    triggerFlash: anchor => set({ flashAnchor: anchor }),
    clearFlash:   ()     => set({ flashAnchor: null }),
    openSidebar:  ()     => set({ sidebarOpen: true }),
    closeSidebar: ()     => set({ sidebarOpen: false }),

    requestChat: prefill => set(s => ({
        chatRequest: { seq: (s.chatRequest?.seq ?? 0) + 1, prefill },
    })),
}));
